import React from "react";
import { FaClock } from "react-icons/fa";
import { FaUser } from "react-icons/fa";
import { FaHandshake } from "react-icons/fa";
import { FaThumbsUp } from "react-icons/fa";
import { FaThumbsDown } from "react-icons/fa";

import useTranslation from "next-translate/useTranslation";
import {
  AvatarTypes,
  ModeIds,
  ResultTypes,
  VariantTypes,
} from "@chesshotel/constants";
import { formatTime, formatDate } from "../../util/util";
import styles from "./PlayedGames.module.css";
import Link from "next/link";
import { getGames, PlayedGame, useStats } from "../../redux/stats";
import { doUpdateGame, updateElo, doEndGame } from "../../redux/gamePlay";
import { FREE_TIER } from "@chesshotel/constants";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { useRouter } from "next/router";
import { useMembership } from "../../redux/membership";
import { useUser } from "../../redux/user";
import { useSiteData } from "../../redux/siteData";
import DisplayBoard from "../../chessBoard/components/DisplayBoard";
import Spinner from "../../components/ui/spinner";
import { useSettings } from "@/redux/settings";
// import { GamesArchive } from "@chesshotel/models";

export function gotoGame(gameData: PlayedGame) {
  import("@chesshotel/chesslib").then((module) => {
    const { Chess } = module;

    const chess =
      gameData.variantId === VariantTypes.VARIANT_CHESS
        ? Chess()
        : Chess(gameData.startFen);

    const moves = gameData.moves;
    const firstPos =
      gameData.variantId === VariantTypes.VARIANT_CHESS
        ? chess.fen()
        : gameData.startFen;
    const positions = moves.map((move) => {
      chess.move(move);
      const fen = chess.fen();
      return fen;
    });
    positions.unshift(firstPos);

    const playingWhite = gameData.playingWhite;
    const possibleMoves = {};
    const turns = moves.length;
    const isUserTurn =
      (turns % 2 === 0 && playingWhite) || (turns % 2 === 1 && !playingWhite);
    const userTime = 0;
    const opponentTime = 0;
    const startTime = gameData.time;
    const startInc = gameData.inc;
    const variantId = gameData.variantId;
    const gameId = gameData.gameId;
    const gameEnded = true;
    const elo = gameData.userOldElo;
    const leaguePlay = playingWhite
      ? gameData.whiteLeagueGame
      : gameData.blackLeagueGame;
    const opponent = {
      id: gameData.opponentId,
      username: gameData.opponentName, // hack for removing prefix later
      displayName: gameData.opponentName,
      fbId: null,
      avatarId: null,
      avatarType: AvatarTypes.DEFAULT, // TODO get from DB
      elo: gameData.opponentOldElo,
      lastServerTime: null,
      currentTime: null,
      timeUpdatedAt: null,
      isGuest: null,
      leaguePlay: null,
    };

    doUpdateGame({
      isUserTurn,
      turns,
      userTime,
      opponentTime,
      opponent,
      playingWhite,
      leaguePlay,
      startTime,
      startInc,
      variantId,
      gameId,
      moves,
      positions,
      elo,
      possibleMoves,
      gameEnded,
      displayGame: true,
      date: gameData.created,
      showAbort: false,
      positionIndex: moves.length,
    });

    updateElo({
      userNewElo: gameData.userNewElo,
      userOldElo: gameData.userOldElo,
      opponentNewElo: gameData.opponentNewElo,
      opponentOldElo: gameData.opponentOldElo,
    });

    doEndGame({ status: gameData.resultTypeId, result: gameData.resultId });
  });
}

export default function PlayedGames() {
  const { t } = useTranslation("common");
  const { t: profileT } = useTranslation("auth");

  const router = useRouter();
  const { page, modeId } = router.query;
  const { memberLoggedIn } = useUser();
  const { chessSet } = useSettings();
  const mode = Number(modeId);

  const { viewedGames, totalGames, gamesLoaded } = useStats();
  const { tier } = useMembership();
  const { id } = useUser();
  const { gamePath } = useSiteData();

  useEffect(() => {
    if (memberLoggedIn) {
      if (page) {
        const p = Number(page);
        getGames(p, mode, tier);
      } else {
        getGames(0, mode, tier);
      }
    }
  }, [page, memberLoggedIn, tier, mode]);

  function renderPageLinks() {
    const total = totalGames[mode];

    const thePage = page ? Number(page) : 0;
    const rows = 10;

    const pages = Math.ceil(total / rows);
    const links = [];
    for (let i = 0; i < pages; i++) {
      if (i === thePage) {
        links.push(
          <span key={i} className={styles.currentPage}>
            {i + 1}{" "}
          </span>,
        );
      } else {
        links.push(
          <Link key={i} href={`/profile?modeId=${modeId}&page=${i}`}>
            {i + 1}
          </Link>,
        );
      }
    }
    return links.length > 0 && links;
  }

  function renderGames() {
    const isDesktop = window.innerWidth > 768;
    const boardSize = isDesktop ? 240 : Math.round(window.innerWidth / 3);

    return viewedGames.map((game) => {
      const playingWhite = game.whiteId === id;
      let resultTitle = t("Draw");
      let whiteScore = 1;
      let blackScore = 1;
      let resultIcon = <FaHandshake />;
      if (game.resultId === ResultTypes.WHITE_WINNS) {
        resultTitle = t("White wins");
        whiteScore = 1;
        blackScore = 0;
        resultIcon = playingWhite ? <FaThumbsUp /> : <FaThumbsDown />;
      } else if (game.resultId === ResultTypes.BLACK_WINNS) {
        resultTitle = t("Black wins");
        whiteScore = 0;
        blackScore = 1;
        resultIcon = playingWhite ? <FaThumbsDown /> : <FaThumbsUp />;
      }

      const whiteEloDiff = game.whiteNewElo - game.whiteOldElo;
      const whiteEloDiffText =
        whiteEloDiff > 0 ? "+" + whiteEloDiff : whiteEloDiff;
      const blackEloDiff = game.blackNewElo - game.blackOldElo;
      const blackEloDiffText =
        blackEloDiff > 0 ? "+" + blackEloDiff : blackEloDiff;

      const lastPos = game.positions[game.positions.length - 1];
      return (
        <div
          key={game.id}
          className={styles.game}
          onClick={() => {
            gotoGame(game);
            router.push(gamePath);
          }}
        >
          <div className={styles.board}>
            <DisplayBoard
              fen={lastPos}
              // lastMove={null}
              playingWhite={playingWhite}
              boardSize={boardSize}
              chessSet={chessSet}
              // pieceImages={pieceImages}
            />
          </div>
          <div className={styles.info}>
            <div className={styles.gameHeader}>
              <div className="flex items-center space-x-1 text-sm">
                <div>
                  <FaClock />
                </div>
                <div>
                  {game.timeTypeId == ModeIds.DAILY
                    ? t("days", { count: game.time })
                    : formatTime(game.time, game.inc)}
                </div>
              </div>
              <div className="text-sm">{formatDate(game.created)}</div>
            </div>
            <div className={styles.resultCont}>
              <div className={styles.opponent}>
                vs
                <br />
                <FaUser /> {game.opponentName}
              </div>
              <div className={styles.title}>{resultTitle}</div>
              <div className={styles.resultInnerCont}>
                <div className={styles.whiteCont}>
                  <div className={styles.usernameCont}>
                    <div className={styles.username}>{game.whiteUsername}</div>
                    <div className={styles.rating}>
                      {game.whiteNewElo} ({whiteEloDiffText})
                    </div>
                  </div>
                  <div className={styles.result}>{whiteScore}</div>
                </div>
                <div className={styles.divider}></div>
                <div className={styles.blackCont}>
                  <div className={styles.result}>{blackScore}</div>
                  <div className={styles.usernameCont}>
                    <div className={styles.username}>{game.blackUsername}</div>
                    <div className={styles.rating}>
                      {game.blackNewElo} ({blackEloDiffText})
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.resultIcon}>{resultIcon}</div>
            </div>
          </div>
        </div>
      );
    });
  }

  function renderOverlay() {
    return (
      <div className={styles.overlay}>
        <Link href="/memberships">
          <Button variant="default" size="wide">
            {t("Get premium")}
          </Button>
        </Link>
      </div>
    );
  }

  if (!gamesLoaded || (viewedGames && viewedGames.length === 0)) {
    return null;
  }

  const pageLinks = renderPageLinks();

  return (
    <div className={styles.cont + " relative z-10"}>
      {tier === FREE_TIER && renderOverlay()}
      <div className={styles.seasonTitle}>{t("Games").toUpperCase()}</div>
      {gamesLoaded ? (
        <>
          {pageLinks && (
            <div className="flex flex-wrap p-3 space-x-1">{pageLinks}</div>
          )}
          <div className={styles.games}>{renderGames()}</div>
          {pageLinks && (
            <div className="flex flex-wrap p-3 space-x-1">{pageLinks}</div>
          )}
        </>
      ) : (
        <div className="flex items-center justify-center w-full h-24">
          <Spinner blue />
        </div>
      )}
    </div>
  );
}
